import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { RetailersApi } from '@core/api';
import * as Entities from '@shared/entities';
import { Result } from '@shared/models';
import { extractErrorMessage } from '@shared/utils';
import { commandResult, emptyListResponse, failureResult } from './store-helpers';

export const RetailersStore = signalStore(
  { providedIn: 'root' },
  withState({
    retailers: emptyListResponse<Entities.Retailer>(),
    simpleRetailers: [] as Entities.RetailerSimple[],
    notes: emptyListResponse<Entities.RetailerNote>(),
    programs: emptyListResponse<Entities.RetailerProgram>(),
    activeRetailer: null as Entities.Retailer | null,
    historyRefreshVersion: 0,
    loadedRetailers: false,
    loadedSimpleRetailers: false,
    loadingRetailers: false,
    loadingSimpleRetailers: false,
    loadingActiveRetailer: false,
    loadingNotes: false,
    loadingPrograms: false,
    notesRetailerId: null as number | null,
    programsRetailerId: null as number | null,
    commandInProgress: false,
    lastCommandName: null as string | null,
    lastCommandResult: null as Result<unknown> | null,
    errorMessage: null as string | null,
  }),
  withComputed(({ retailers, simpleRetailers, notes, programs }) => ({
    retailerRecords: computed(() => retailers().records),
    simpleRetailerRecords: computed(() => simpleRetailers()),
    noteRecords: computed(() => notes().records),
    programRecords: computed(() => programs().records),
    retailerById: computed(() => new Map(retailers().records.map((retailer) => [retailer.retailerId, retailer]))),
  })),
  withMethods((store, retailersApi = inject(RetailersApi)) => {
    const refreshHistory = (): void => {
      patchState(store, { historyRefreshVersion: store.historyRefreshVersion() + 1 });
    };

    let retailersRequestId = 0;
    let simpleRetailersRequestId = 0;
    let activeRetailerRequestId = 0;
    let notesRequestId = 0;
    let programsRequestId = 0;

    const loadRetailers = (force = false): void => {
      if (!force && store.loadedRetailers()) {
        return;
      }

      const requestId = ++retailersRequestId;
      patchState(store, { loadingRetailers: true, errorMessage: null });

      void retailersApi
        .loadRetailers(force)
        .then((retailers) => {
          if (requestId === retailersRequestId) {
            patchState(store, { retailers, loadedRetailers: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === retailersRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load retailers.') });
          }
        })
        .finally(() => {
          if (requestId === retailersRequestId) {
            patchState(store, { loadingRetailers: false });
          }
        });
    };

    const loadSimpleRetailers = (force = false): void => {
      if (!force && store.loadedSimpleRetailers()) {
        return;
      }

      const requestId = ++simpleRetailersRequestId;
      patchState(store, { loadingSimpleRetailers: true, errorMessage: null });

      void firstValueFrom(retailersApi.fetchAll())
        .then((simpleRetailers) => {
          if (requestId === simpleRetailersRequestId) {
            patchState(store, { simpleRetailers, loadedSimpleRetailers: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === simpleRetailersRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load retailers.') });
          }
        })
        .finally(() => {
          if (requestId === simpleRetailersRequestId) {
            patchState(store, { loadingSimpleRetailers: false });
          }
        });
    };

    const loadActiveRetailer = (id: number): void => {
      const requestId = ++activeRetailerRequestId;
      patchState(store, { loadingActiveRetailer: true, errorMessage: null });

      void firstValueFrom(retailersApi.fetchRetailer(id))
        .then((activeRetailer) => {
          if (requestId === activeRetailerRequestId) {
            patchState(store, { activeRetailer });
          }
        })
        .catch((error: unknown) => {
          if (requestId === activeRetailerRequestId) {
            patchState(store, {
              activeRetailer: null,
              errorMessage: extractErrorMessage(error, 'Unable to load retailer.'),
            });
          }
        })
        .finally(() => {
          if (requestId === activeRetailerRequestId) {
            patchState(store, { loadingActiveRetailer: false });
          }
        });
    };

    const loadNotes = (force: boolean, retailerId: number): void => {
      if (!force && store.notesRetailerId() === retailerId) {
        return;
      }

      const requestId = ++notesRequestId;
      patchState(store, { loadingNotes: true, errorMessage: null });

      void retailersApi
        .loadNotes(force, retailerId)
        .then((notes) => {
          if (requestId === notesRequestId) {
            patchState(store, { notes, notesRetailerId: retailerId });
          }
        })
        .catch((error: unknown) => {
          if (requestId === notesRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load retailer notes.') });
          }
        })
        .finally(() => {
          if (requestId === notesRequestId) {
            patchState(store, { loadingNotes: false });
          }
        });
    };

    const loadPrograms = (force: boolean, retailerId: number): void => {
      if (!force && store.programsRetailerId() === retailerId) {
        return;
      }

      const requestId = ++programsRequestId;
      patchState(store, { loadingPrograms: true, errorMessage: null });

      void retailersApi
        .loadPrograms(force, retailerId)
        .then((programs) => {
          if (requestId === programsRequestId) {
            patchState(store, { programs, programsRetailerId: retailerId });
          }
        })
        .catch((error: unknown) => {
          if (requestId === programsRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load retailer programs.') });
          }
        })
        .finally(() => {
          if (requestId === programsRequestId) {
            patchState(store, { loadingPrograms: false });
          }
        });
    };

    return {
      loadRetailers,
      loadSimpleRetailers,
      loadActiveRetailer,
      loadNotes,
      loadPrograms,
      setActiveRetailer(id: number): void {
        const retailer = store.retailerRecords().find((record) => record.retailerId === id);
        patchState(store, { activeRetailer: retailer ?? null });
      },
      getRetailerProgram(id: number): Entities.RetailerProgram | undefined {
        return store.programRecords().find((program) => program.retailerProgramId === id);
      },
      create(retailer: Entities.Retailer): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'create',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailersApi
          .create(retailer)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('create', result) });
            if (result.successful) {
              loadRetailers(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to create retailer.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to create retailer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('create', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      update(retailerId: number, retailer: Entities.Retailer): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'update',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailersApi
          .update(retailerId, retailer)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('update', result) });
            if (result.successful) {
              loadRetailers(true);
              loadActiveRetailer(retailerId);
              refreshHistory();
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to update retailer.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to update retailer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('update', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      activateProgram(retailerId: number, programId: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'activateProgram',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailersApi
          .activateProgram(retailerId, programId)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('activateProgram', result) });
            if (result.successful) {
              loadPrograms(true, retailerId);
              refreshHistory();
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to activate program retailer.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to activate program retailer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('activateProgram', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      activatePhoneNumber(retailerId: number, retailerProgramId: number, callCenterPhoneNumberId: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'activatePhoneNumber',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailersApi
          .activatePhoneNumber(retailerId, retailerProgramId, callCenterPhoneNumberId)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('activatePhoneNumber', result) });
            if (result.successful) {
              refreshHistory();
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to activate phone number retailer.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to activate phone number retailer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('activatePhoneNumber', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      deactivate(retailerId: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'deactivate',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailersApi
          .deactivate(retailerId)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('deactivate', result) });
            if (result.successful) {
              loadRetailers(true);
              refreshHistory();
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to deactivate retailer.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to deactivate retailer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('deactivate', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      deactivateProgram(retailerId: number, programId: number, retailerProgramId = 0): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'deactivateProgram',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailersApi
          .deactivateProgram(retailerId, programId, retailerProgramId)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('deactivateProgram', result) });
            if (result.successful) {
              loadPrograms(true, retailerId);
              refreshHistory();
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to deactivate program retailer.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to deactivate program retailer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('deactivateProgram', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      deactivatePhoneNumber(retailerId: number, retailerPhoneNumberId: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'deactivatePhoneNumber',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailersApi
          .deactivatePhoneNumber(retailerId, retailerPhoneNumberId)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('deactivatePhoneNumber', result) });
            if (result.successful) {
              refreshHistory();
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to deactivate phone number retailer.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to deactivate phone number retailer.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('deactivatePhoneNumber', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      createRetailerNote(retailerId: number, retailerNote: Entities.RetailerNote): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'createRetailerNote',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailersApi
          .createRetailerNote(retailerId, retailerNote)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('createRetailerNote', result) });
            if (result.successful) {
              loadNotes(true, retailerId);
              refreshHistory();
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to create retailer note.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to create retailer note.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('createRetailerNote', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      updateRetailerNote(retailerId: number, retailerNote: Entities.RetailerNote): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'updateRetailerNote',
          lastCommandResult: null,
          errorMessage: null,
        });

        void retailersApi
          .updateRetailerNote(retailerId, retailerNote)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('updateRetailerNote', result) });
            if (result.successful) {
              loadNotes(true, retailerId);
              refreshHistory();
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to update retailer note.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to update retailer note.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('updateRetailerNote', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      clearActiveRetailer(): void {
        patchState(store, { activeRetailer: null });
      },
      clearNotes(): void {
        patchState(store, { notes: emptyListResponse<Entities.RetailerNote>(), notesRetailerId: null });
      },
      clearPrograms(): void {
        patchState(store, { programs: emptyListResponse<Entities.RetailerProgram>(), programsRetailerId: null });
      },
      clearCommandResult(): void {
        patchState(store, { lastCommandName: null, lastCommandResult: null });
      },
    };
  })
);
